import { z } from 'zod';

/**
 * Retail Media: Zahlung per hinterlegtem Zahlungsmittel (Kreditkarte oder PayPal) statt nur Vorkasse.
 *
 * Mit gültigem Zahlungsmittel darf der Saldo eines Werbekontos bis zum Kreditrahmen ins Minus laufen – die Anzeigen
 * laufen ohne Aufladung weiter. Der Marktplatz bucht die offenen Kosten (negativer Saldo) ab, sobald sie die
 * Schwelle erreichen, spätestens am Monatsletzten. Schlägt eine Abbuchung fehl, gilt kein Kreditrahmen mehr: die
 * Kampagnen pausieren, bis eine Zahlung klappt (neuer Versuch, neues Zahlungsmittel oder Aufladung).
 */

export const ZAHLUNGSMITTEL_ARTEN = { karte: 'Kreditkarte', paypal: 'PayPal' } as const;
export type ZahlungsmittelArt = keyof typeof ZAHLUNGSMITTEL_ARTEN;

export const ZAHLUNGSMITTEL_STATUS = { keins: 'Nicht hinterlegt', aktiv: 'Aktiv', fehlgeschlagen: 'Abbuchung fehlgeschlagen' } as const;
export type ZahlungsmittelStatus = keyof typeof ZAHLUNGSMITTEL_STATUS;

export const ABBUCHUNG_STATUS = { offen: 'In Bearbeitung', bezahlt: 'Bezahlt', fehlgeschlagen: 'Fehlgeschlagen' } as const;
export type AbbuchungStatus = keyof typeof ABBUCHUNG_STATUS;

export const ABBUCHUNG_GRUENDE = { schwelle: 'Schwelle erreicht', monatsende: 'Monatsende', wiederholung: 'Neuer Versuch', manuell: 'Sofort bezahlt' } as const;
export type AbbuchungGrund = keyof typeof ABBUCHUNG_GRUENDE;

/** Abbuchung, sobald die offenen Kosten diesen Betrag erreichen (höchstens der Kreditrahmen) */
export const ABBUCHUNG_SCHWELLE_CENT = 5_000;
/** Kleinster abbuchbarer Betrag – darunter wandern die Kosten in den nächsten Monat */
export const ABBUCHUNG_MIN_CENT = 50;
/** Automatische neue Versuche nach einem Fehlschlag (je einer pro Tag) */
export const ABBUCHUNG_VERSUCHE = 3;
export const KREDITRAHMEN_STANDARD_CENT = 20_000;
export const KREDITRAHMEN_MAX_CENT = 1_000_000;

/** Schwelle eines Werbekontos: 50 €, bei kleinerem Kreditrahmen der Rahmen selbst */
export const abbuchungSchwelle = (kreditrahmenCent: number) => Math.max(ABBUCHUNG_MIN_CENT, Math.min(ABBUCHUNG_SCHWELLE_CENT, kreditrahmenCent));

export const KreditrahmenSchema = z.object({
  cent: z.number({ invalid_type_error: 'Bitte Betrag angeben' }).int('Bitte Betrag in Cent angeben')
    .min(0, 'Nicht unter 0 €')
    .max(KREDITRAHMEN_MAX_CENT, `Höchstens ${(KREDITRAHMEN_MAX_CENT / 100).toLocaleString('de-DE')} €`),
});
export type Kreditrahmen = z.infer<typeof KreditrahmenSchema>;

export const AbbuchungenAbfrage = z.object({
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export interface Zahlungsmittel {
  art: ZahlungsmittelArt;
  /** z. B. „Visa •••• 4242 (12/28)“ oder „PayPal · name@beispiel.de“ */
  bezeichnung: string;
  testmodus: boolean;
  hinterlegtAm: string | null;
}

export interface Abbuchung {
  id: string;
  cent: number;
  grund: AbbuchungGrund;
  status: AbbuchungStatus;
  /** Bezeichnung des Zahlungsmittels zum Zeitpunkt der Abbuchung */
  zahlungsmittel: string;
  /** Grund des Fehlschlags (Text des Zahlungsanbieters) */
  fehler: string;
  testmodus: boolean;
  erstelltAm: string;
  bezahltAm: string | null;
}

export interface AbbuchungSeite {
  eintraege: Abbuchung[];
  weiter: string | null;
}

/** Zahlung eines Werbekontos – Portal (Guthaben & Zahlung) und Konto (Werbekonto-Detail) */
export interface WerbeZahlung {
  /** Marktplatz hat die Zahlung eingerichtet (Stripe-Schlüssel) – sonst lässt sich nichts hinterlegen */
  moeglich: boolean;
  status: ZahlungsmittelStatus;
  zahlungsmittel: Zahlungsmittel | null;
  /** Hinterlegen begonnen, aber noch nicht abgeschlossen */
  einrichtungOffen: boolean;
  kreditrahmenCent: number;
  /** wirksam: kreditrahmenCent bei aktivem Zahlungsmittel, sonst 0 */
  kreditCent: number;
  /** negativer Saldo – was beim nächsten Mal abgebucht wird */
  offeneKostenCent: number;
  schwelleCent: number;
  /** spätestens an diesem Tag (JJJJ-MM-TT, Monatsletzter) */
  naechsteAbbuchungAm: string | null;
  /** nach einem Fehlschlag: nächster automatischer Versuch */
  naechsterVersuchAm: string | null;
  fehler: string;
  /** Abbuchung gerade in Bearbeitung */
  abbuchungLaeuft: boolean;
}

/** Antwort beim Hinterlegen: Weiterleitung zur Eingabe des Zahlungsmittels */
export interface ZahlungsmittelWeiterleitung {
  url: string;
}
